/* ============================================
   APPLICATION CONSTANTS
   The Creative Orchestra - GEMINI Framework
   ============================================ */

// Site Information
export const SITE_INFO = {
  title: 'The Creative Orchestra',
  subtitle: "A Designer's Quest to Humanize Artificial Intelligence",
  tagline: 'The Story of GEMINI: Where Human Dreams Meet Machine Intelligence',
  author: 'Sankar Balasubramanian',
  institution: 'Indian Institute of Science (IISc), Bangalore',
  yearStart: 2020,
  yearEnd: 2025,
  year: '2020-2025',
};

// Author Information
export const AUTHOR_INFO = {
  name: 'Sankar Balasubramanian',
  title: 'PhD Researcher',
  institution: 'Indian Institute of Science (IISc), Bangalore',
  email: 'sankarb@iisc.ac.in',
  linkedin: 'https://www.linkedin.com/in/sankar4',
  website: 'https://www.sankar.studio',
  photo: '/assets/images/profile/sankar.jpg',
};

// Navigation Links
export const NAV_LINKS = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'novel', label: 'Novel', path: '/novel' },
  { id: 'thesis', label: 'Thesis', path: '/thesis' },
  {
    id: 'works',
    label: 'Works',
    path: '/works',
    children: [
      { id: 'gemini', label: 'GEMINI', path: '/work/gemini' },
      { id: 'genie', label: 'GENIE', path: '/work/genie' },
      { id: 'midas', label: 'MIDAS', path: '/work/midas' },
      { id: 'magics', label: 'MAGICS', path: '/work/magics' },
      { id: 'dimes', label: 'DIMES', path: '/work/dimes' },
      { id: 'aegis', label: 'AEGIS', path: '/work/aegis' },
      { id: 'traces', label: 'TRACES', path: '/work/traces' },
      { id: 'euphoria', label: 'EUPHORIA', path: '/work/euphoria' },
      { id: 'retina', label: 'RETINA', path: '/work/retina' },
      { id: 'fenso', label: 'FENSO', path: '/work/fenso' },
    ],
  },
  { id: 'about', label: 'About', path: '/about' },
  { id: 'publications', label: 'Publications', path: '/publications' },
  { id: 'acknowledgements', label: 'Acknowledgements', path: '/acknowledgements' },
];

// GEMINI Framework Stages
export const GEMINI_STAGES = {
  FRAMEWORK: {
    id: 'framework',
    name: 'Framework',
    letter: 'GEMINI',
    description: 'The overarching ecosystem of Creative Machines',
    works: ['gemini'],
  },
  GENERATION: {
    id: 'generation',
    name: 'Generation',
    letter: 'G',
    description: 'Creating and expanding the solution space',
    works: ['genie', 'midas'],
  },
  EVALUATION: {
    id: 'evaluation',
    name: 'Evaluation',
    letter: 'E',
    description: 'Assessing and navigating idea landscapes',
    works: ['magics'],
  },
  MANAGEMENT: {
    id: 'management',
    name: 'Management',
    letter: 'M',
    description: 'Tracking the evolution of design intent',
    works: ['dimes', 'aegis', 'traces'],
  },
  INSPIRATION: {
    id: 'inspiration',
    name: 'INspiration',
    letter: 'IN',
    description: 'Transforming attention into form',
    works: ['euphoria', 'retina', 'fenso'],
  },
};

// Works Order (grouped by GEMINI stages)
export const WORKS_ORDER = [
  // Framework
  'gemini',
  // Generation
  'genie',
  'midas',
  // Evaluation
  'magics',
  // Management
  'dimes',
  'aegis',
  'traces',
  // Inspiration
  'euphoria',
  'retina',
  'fenso',
];

// Work Link Types
export const LINK_TYPES = {
  website: { label: 'Live Platform', icon: 'Globe' },
  download: { label: 'Download', icon: 'Download' },
  demo: { label: 'Demo', icon: 'Play' },
  paper: { label: 'Research Paper', icon: 'FileText' },
  documentation: { label: 'Documentation', icon: 'BookOpen' },
  github: { label: 'GitHub', icon: 'Github' },
  userGuide: { label: 'User Guide', icon: 'ClipboardList' },
  model: { label: 'Model', icon: 'Brain' },
  dataset: { label: 'Dataset', icon: 'Database' },
};

// Chapter Parts (for novel navigation)
export const NOVEL_PARTS = [
  {
    id: 'part-1',
    title: 'Part One: The Awakening',
    subtitle: 'Understanding the Problem',
    chapters: ['01', '02', '03', '04'],
  },
  {
    id: 'part-2',
    title: 'Part Two: Building the Language',
    subtitle: 'Creating the Grammar of Design',
    chapters: ['05', '06', '07'],
  },
  {
    id: 'part-3',
    title: 'Part Three: The Sea of Ideas',
    subtitle: 'Navigating the Abundance',
    chapters: ['08', '09', '10'],
  },
  {
    id: 'part-4',
    title: 'Part Four: The Progressive Revolution',
    subtitle: 'From Single Spurt to Continuous Creation',
    chapters: ['11', '12', '13'],
  },
  {
    id: 'part-5',
    title: "Part Five: The Sketcher's Dilemma",
    subtitle: 'Capturing the Evolution of Form',
    chapters: ['14', '15', '16', '17', '18'],
  },
  {
    id: 'part-6',
    title: 'Part Six: The Eye of the Beholder',
    subtitle: 'Attention as the Language of Aesthetics',
    chapters: ['19', '20', '21', '22', '23'],
  },
  {
    id: 'part-7',
    title: 'Part Seven: The Symphony',
    subtitle: 'Complete Human-AI Co-Creation',
    chapters: ['24', '25', '26'],
  },
  {
    id: 'part-8',
    title: 'Part Eight: The GEMINI Framework',
    subtitle: 'A New Paradigm for Design',
    chapters: ['27', '28', '29'],
  },
  {
    id: 'part-9',
    title: 'Part Nine: Reflections and Horizons',
    subtitle: 'Looking Back and Forward',
    chapters: ['30', '31', '32'],
  },
];

// Animation Durations (in seconds)
export const ANIMATION_DURATIONS = {
  fast: 0.15,
  base: 0.3,
  slow: 0.5,
  verySlow: 1,
  orbit: 30,
  twinkle: 3,
  float: 6,
  spectrum: 2,
};

// Breakpoints (matching Tailwind)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

// Local Storage Keys
export const STORAGE_KEYS = {
  bookmark: 'gemini-novel-bookmark',
  readingProgress: 'gemini-reading-progress',
  audioMuted: 'gemini-audio-muted',
  audioVolume: 'gemini-audio-volume',
  theme: 'gemini-theme-preference',
  visitedChapters: 'gemini-visited-chapters',
};

// Audio Configuration
export const AUDIO_CONFIG = {
  defaultVolume: 0.3,
  fadeInDuration: 2000,
  fadeOutDuration: 1000,
  ambientTrack: '/assets/audio/ambient-orchestra.mp3',
};

// Orbital Configuration (for landing page)
export const ORBITAL_CONFIG = {
  centerOrbSize: 120, // pixels
  workOrbSize: 80, // pixels
  orbitRadius: 280, // pixels from center
  orbitDuration: 60, // seconds for full rotation
  hoverScale: 1.15,
};

// Constellation Configuration
export const CONSTELLATION_CONFIG = {
  letters: ['G', 'E', 'M', 'I', 'N', 'I'],
  starCount: 150, // background stars
  letterStarSize: 4, // pixels
  backgroundStarSizeRange: [1, 3], // min, max pixels
  connectionOpacity: 0.3,
};

// Spectrum Visualizer Configuration
export const SPECTRUM_CONFIG = {
  barCount: 32,
  minHeight: 10, // percentage
  maxHeight: 100, // percentage
  barWidth: 4, // pixels
  barGap: 2, // pixels
  animationSpeed: 150, // ms between updates
};

// PDF Viewer Configuration
export const PDF_CONFIG = {
  defaultScale: 1,
  minScale: 0.5,
  maxScale: 3,
  scaleStep: 0.25,
};

// Image Gallery Configuration
export const GALLERY_CONFIG = {
  thumbnailSize: 200,
  lightboxTransitionDuration: 300,
  swipeThreshold: 50,
};

// API Endpoints (for future backend)
export const API_ENDPOINTS = {
  base: '/api',
  chapters: '/api/chapters',
  works: '/api/works',
  analytics: '/api/analytics',
};

// Social Links
export const SOCIAL_LINKS = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    url: 'https://www.linkedin.com/in/sankar4',
    icon: 'Linkedin',
  },
  {
    id: 'website',
    label: 'Website',
    url: 'https://www.sankar.studio',
    icon: 'Globe',
  },
  {
    id: 'email',
    label: 'Email',
    url: 'mailto:sankarb@iisc.ac.in',
    icon: 'Mail',
  },
];

// Error Messages
export const ERROR_MESSAGES = {
  chapterNotFound: 'Chapter not found. Please check the URL and try again.',
  workNotFound: 'Work not found. Please check the URL and try again.',
  loadingFailed: 'Failed to load content. Please refresh the page.',
  pdfLoadFailed: 'Failed to load PDF. Please try downloading instead.',
};

// Meta Tags Defaults
export const META_DEFAULTS = {
  title: 'The Creative Orchestra | GEMINI Framework',
  description:
    "A Designer's Quest to Humanize Artificial Intelligence. Explore the GEMINI framework for synergistic Human-AI collaboration in product design.",
  keywords:
    'GEMINI, AI, Human-AI Collaboration, Product Design, PhD Research, IISc Bangalore, Creative Machines, Design Ideation',
  image: '/assets/images/og-image.jpg',
};
